import { makeStyles } from "@material-ui/core/styles";
import { buildAgendaTheme } from "../../theme/agendaTheme";

// FASE 0: Usar agendaTheme del tema global (0257)
// D3: el fallback también es mode-aware — si `theme.agenda` faltara, se reconstruye
// desde `theme.palette.type` en vez de degradar a una paleta light fija.
const getAgenda = (theme) =>
  theme.agenda || buildAgendaTheme(theme.palette?.type === "dark" ? "dark" : "light");

export const useStyles = makeStyles((theme) => {
  const a = getAgenda(theme);
  const palette = a.palette || {};
  const shape = a.shape || { borderRadius: 8 };
  const shadows = a.shadows || {};

  // Tokens resueltos una sola vez (antes cada regla repetía su propio literal claro)
  const border = palette.border;
  const surface = palette.card;
  const sunken = palette.background;
  const radius = shape.borderRadius || 8;
  const radiusSm = shape.borderRadiusSm || 6;

  return {
    /**
     * F6 — Raíz del módulo.
     * WHY: `CssBaseline` está montado FUERA del ThemeProvider (src/index.js), así que se
     * resuelve con el tema light por defecto de MUI y fija `body { color: rgba(0,0,0,.87) }`
     * incluso en dark. Todo texto que hereda el color quedaba casi negro; solo se leía lo
     * que MUI colorea explícitamente (Paper, o `color="textSecondary"`).
     * `display: contents` no genera caja —no altera el layout— pero sí propaga la herencia.
     */
    agendaRoot: {
      display: "contents",
      color: palette.textPrimary,
    },
    // Tabs (FASE 1)
    tabs: { marginBottom: theme.spacing(2), borderBottom: `1px solid ${border}` },
    // F1: MUI aplica opacity .7 al tab no seleccionado — sobre superficie oscura queda
    // por debajo del umbral legible. Se neutraliza y se da color explícito por token.
    tab: {
      textTransform: "none",
      fontWeight: 600,
      minWidth: 100,
      opacity: 1,
      color: palette.textSecondary,
      "&.Mui-selected": { color: palette.primaryLight },
    },
    badge: { marginLeft: theme.spacing(0.5), fontSize: "0.75rem" },
    /**
     * F2: inputs nativos date / time / datetime-local.
     * WHY: sin `color-scheme`, Chrome pinta el texto del campo, el icono de calendario y
     * TODO el popup del selector con el esquema claro aunque la app esté en dark.
     */
    nativeDateInput: {
      "& input": { colorScheme: a.mode },
    },
    /**
     * F4: enlace-acción montado sobre un <button> nativo.
     * WHY: MUI Typography no fija `color`, así que el botón caía al `buttontext` del
     * navegador (negro) — invisible sobre superficie oscura.
     */
    linkButton: {
      cursor: "pointer",
      textDecoration: "underline",
      border: "none",
      background: "none",
      padding: 0,
      font: "inherit",
      color: palette.primaryLight,
      "&:hover": { color: palette.primary },
    },
    // Pantalla de bienvenida / onboarding (D5)
    emptyStateWrap: { textAlign: "center", padding: "56px 24px 40px", maxWidth: 560, margin: "0 auto" },
    emptyStateIcon: {
      width: 88,
      height: 88,
      borderRadius: "50%",
      margin: "0 auto 24px",
      backgroundColor: palette.selected,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": { fontSize: 48, color: palette.primaryLight },
    },
    // Configuración — layout raíz
    configLayout: {
      display: "flex",
      gap: 0,
      marginTop: theme.spacing(2),
      minHeight: 420,
    },
    // Nav lateral izquierda
    configNav: {
      width: 210,
      flexShrink: 0,
      paddingRight: theme.spacing(1),
      borderRight: `1px solid ${border}`,
    },
    configNavItem: {
      borderRadius: radiusSm,
      marginBottom: 2,
      paddingTop: theme.spacing(0.85),
      paddingBottom: theme.spacing(0.85),
      paddingLeft: theme.spacing(1.25),
      borderLeft: "3px solid transparent",
      transition: "background 0.12s, border-color 0.12s",
      "&.Mui-selected": {
        backgroundColor: palette.selected,
        borderLeftColor: palette.primaryLight,
        "& $configNavIcon": { color: palette.primaryLight },
        "& .MuiListItemText-primary": { fontWeight: 700, color: palette.primaryLight },
      },
      "&:hover:not(.Mui-selected)": { backgroundColor: palette.hover },
    },
    configNavIcon: {
      minWidth: 32,
      color: palette.textMuted,
      "& svg": { fontSize: 18 },
    },
    configContent: { flex: 1, paddingLeft: theme.spacing(3) },
    // Banner de perfil activo (siempre visible arriba del contenido)
    configProfileCard: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(2, 2.5),
      marginBottom: theme.spacing(2.5),
      borderRadius: radius,
      border: `1px solid ${border}`,
      backgroundColor: sunken,
    },
    configProfileCardActions: {
      display: "flex",
      gap: theme.spacing(1),
      flexShrink: 0,
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    // Cabecera de cada sección
    configSectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2),
    },
    // Tarjeta de secuencia
    configSeqCard: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      borderRadius: radiusSm,
      border: `1px solid ${border}`,
      backgroundColor: surface,
      transition: "box-shadow 0.15s",
      "&:hover": { boxShadow: shadows.cardHover },
    },
    configSeqDot: {
      width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
    },
    configSeqInfo: { flex: 1, minWidth: 0 },
    configSeqNameRow: {
      display: "flex", alignItems: "center", gap: theme.spacing(1), flexWrap: "wrap",
    },
    // Vista previa de plantilla dentro de la tarjeta de secuencia (D5)
    configSeqTemplate: {
      color: palette.textMuted,
      fontStyle: "italic",
      display: "block",
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 380,
    },
    // Fila de horario
    configHorariosRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(1.25, 2),
      marginBottom: theme.spacing(0.75),
      borderRadius: radiusSm,
      border: `1px solid ${border}`,
      backgroundColor: surface,
      minHeight: 52,
    },
    configHorariosDay: {
      width: 110,
      flexShrink: 0,
    },
    configHorariosTime: {
      width: 118,
    },
    configHorariosSlotRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
      borderRadius: radiusSm,
      border: `1px dashed ${border}`,
      backgroundColor: sunken,
    },
    // Fila de equipo
    configTeamRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      borderRadius: radiusSm,
      border: `1px solid ${border}`,
      backgroundColor: surface,
      transition: "box-shadow 0.15s",
      "&:hover": { boxShadow: shadows.cardHover },
    },
    configTeamInfo: {
      flex: 1,
      minWidth: 0,
    },
    configTeamNameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexWrap: "wrap",
    },
    // Tarjeta horizontal de servicio
    configSvcCard: {
      display: "flex",
      alignItems: "center",
      border: `1px solid ${border}`,
      borderRadius: radiusSm,
      backgroundColor: surface,
      marginBottom: theme.spacing(1),
      overflow: "hidden",
      transition: "box-shadow 0.15s",
      "&:hover": { boxShadow: shadows.cardHover },
    },
    configSvcAccent: {
      width: 4,
      alignSelf: "stretch",
      minHeight: 56,
      flexShrink: 0,
    },
    configSvcBody: {
      flex: 1,
      padding: theme.spacing(1.25, 1.75),
    },
    configSvcActions: {
      padding: theme.spacing(0, 1.5),
      flexShrink: 0,
    },
    // Iconos de acción y microtipografía de configuración (D5)
    configActionIcon: { fontSize: 20, color: palette.textSecondary },
    configDeleteIcon: { fontSize: 20, color: palette.error },
    configDivider: { borderTop: `1px solid ${border}`, marginBottom: 20 },
    configDividerTop: { borderTop: `1px solid ${border}`, paddingTop: 16 },
    // Antetítulo en versalitas: usaba #9ca3af fijo (2.7:1 sobre blanco). textSecondary pasa AA en ambos modos.
    configEyebrow: {
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: palette.textSecondary,
      fontWeight: 600,
    },
    configDayClosed: { color: palette.textSecondary },
    configClosedLabel: { color: palette.danger, fontWeight: 700, letterSpacing: "0.05em" },

    // AgendaSetupWizard (D5) — el wizard no tenía hoja de estilos: todo iba inline en claro
    wizardCard: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      padding: "14px 18px",
      marginBottom: 10,
      borderRadius: 10,
      border: `2px solid ${border}`,
      backgroundColor: surface,
      cursor: "pointer",
      transition: "border-color 0.15s, background-color 0.15s",
    },
    wizardPanel: {
      padding: "18px 22px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      backgroundColor: surface,
    },
    wizardPanelDashed: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      padding: "10px 16px",
      marginBottom: 16,
      borderRadius: radius,
      border: `1px dashed ${border}`,
      backgroundColor: sunken,
    },
    wizardRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "9px 14px",
      marginBottom: 6,
      borderRadius: radius,
      border: `1px solid ${border}`,
      backgroundColor: surface,
    },
    wizardActions: { padding: "16px 24px", borderTop: `1px solid ${border}` },
    wizardLinkButton: { color: palette.primaryLight },
    wizardStepChip: {
      fontWeight: 700,
      fontSize: "0.75rem",
      backgroundColor: palette.selected,
      color: palette.primaryLight,
      border: `1px solid ${palette.primaryLight}`,
    },
    wizardStepChipFinal: {
      fontWeight: 700,
      fontSize: "0.75rem",
      backgroundColor: palette.confirmed,
      color: "#fff",
      border: "none",
    },
    wizardActivateButton: {
      minWidth: 190,
      backgroundColor: palette.confirmed,
      color: "#fff",
      "&:hover": { backgroundColor: palette.completed },
      "&.Mui-disabled": { backgroundColor: palette.textMuted, color: surface },
    },
    // Compat: clases legacy no eliminadas (usadas desde otros tabs)
    configCard: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: radius,
      border: `1px solid ${border}`,
      backgroundColor: surface,
    },
    configCardTitle: {
      fontSize: (a.typography && a.typography.h4 && a.typography.h4.fontSize) || "1.125rem",
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
    configCardDesc: {
      fontSize: (a.typography && a.typography.body2 && a.typography.body2.fontSize) || "0.875rem",
      color: palette.textSecondary,
      marginBottom: theme.spacing(2),
    },
    configCardActions: { display: "flex", justifyContent: "flex-end" },
    configProfileBanner: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: radius,
      backgroundColor: sunken,
      border: `1px solid ${border}`,
    },
    configFirstTimeBanner: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: radius,
      backgroundColor: palette.selected,
      border: `1px dashed ${palette.primaryLight}`,
    },
    // Dashboard (FASE 2)
    dashboardKpi: {
      padding: theme.spacing(2),
      borderRadius: radius,
      backgroundColor: surface,
      boxShadow: shadows.card,
      border: `1px solid ${border}`
    },
    dashboardChart: {
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
      borderRadius: radius,
      backgroundColor: surface,
      boxShadow: shadows.card,
      border: `1px solid ${border}`
    },
    dashboardToday: {
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
      borderRadius: radius,
      backgroundColor: surface,
      boxShadow: shadows.card,
      border: `1px solid ${border}`
    },
    dashboardTodayItem: {
      padding: theme.spacing(1.5, 0),
      borderBottom: `1px solid ${border}`,
      "&:last-child": { borderBottom: "none" }
    },
    // Píldora de estado tintada (Dashboard / Historial) — D4
    statusPill: {
      fontSize: "0.75rem",
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: radiusSm,
      border: "1px solid transparent",
      whiteSpace: "nowrap",
    },
    statusPillSm: {
      fontSize: "0.7rem",
      fontWeight: 600,
      padding: "2px 6px",
      borderRadius: radiusSm,
      border: "1px solid transparent",
      whiteSpace: "nowrap",
    },
    viewToggle: { marginRight: theme.spacing(2) },
    boardPaper: {
      marginTop: theme.spacing(2),
      overflow: "auto",
      borderRadius: radius,
      boxShadow: shadows.card,
      "&:hover": { boxShadow: shadows.cardHover }
    },

    /**
     * Calendario — D3.
     * WHY: `react-big-calendar/lib/css/react-big-calendar.css` es light-only y el módulo
     * no tenía ni un override. En dark eso dejaba `.rbc-off-range-bg: #e6e6e6`,
     * `.rbc-today: #eaf6ff` y `.rbc-toolbar button { color:#373a3c; border:#ccc }` — de ahí
     * las bandas grises, la celda de hoy azul pálido y la barra de navegación ilegible.
     * Todas las reglas van anidadas bajo esta clase: no se emite CSS global.
     */
    calendarWrap: {
      height: 600,
      marginTop: theme.spacing(2),
      borderRadius: radius,
      overflow: "hidden",
      border: `1px solid ${border}`,
      backgroundColor: surface,
      color: palette.textPrimary,

      // ── Barra de navegación ──
      "& .rbc-toolbar": {
        marginBottom: theme.spacing(1.5),
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 1.5, 0),
      },
      "& .rbc-toolbar .rbc-toolbar-label": {
        fontWeight: 600,
        color: palette.textPrimary,
        textTransform: "capitalize",
      },
      "& .rbc-toolbar button": {
        color: palette.textPrimary,
        backgroundColor: surface,
        border: `1px solid ${border}`,
        transition: "background-color .15s, color .15s, border-color .15s",
      },
      "& .rbc-toolbar button:hover, & .rbc-toolbar button:focus": {
        backgroundColor: palette.hover,
        borderColor: palette.borderStrong,
        color: palette.textPrimary,
      },
      "& .rbc-toolbar button:active, & .rbc-toolbar button.rbc-active": {
        backgroundColor: palette.primaryLight,
        borderColor: palette.primaryLight,
        color: palette.primaryContrast,
        boxShadow: "none",
      },
      "& .rbc-toolbar button:active:hover, & .rbc-toolbar button:active:focus, & .rbc-toolbar button.rbc-active:hover, & .rbc-toolbar button.rbc-active:focus": {
        backgroundColor: palette.primaryLight,
        borderColor: palette.primaryLight,
        color: palette.primaryContrast,
      },
      "& .rbc-toolbar button[disabled], & .rbc-toolbar button:disabled": {
        color: palette.textMuted,
        opacity: 0.6,
      },

      // ── Rejilla común ──
      "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
        border: `1px solid ${border}`,
        backgroundColor: surface,
      },
      "& .rbc-header": {
        borderBottom: `1px solid ${border}`,
        color: palette.textSecondary,
        fontWeight: 600,
        padding: "8px 4px",
        textTransform: "capitalize",
      },
      "& .rbc-header + .rbc-header": { borderLeft: `1px solid ${border}` },
      "& .rbc-month-row + .rbc-month-row": { borderTop: `1px solid ${border}` },
      "& .rbc-day-bg + .rbc-day-bg": { borderLeft: `1px solid ${border}` },
      "& .rbc-date-cell": { color: palette.textSecondary, padding: "4px 6px" },
      "& .rbc-date-cell.rbc-now": { color: palette.primaryLight, fontWeight: 700 },
      "& .rbc-off-range-bg": { backgroundColor: palette.calendarOffRange },
      "& .rbc-off-range": { color: palette.calendarOffRangeText },
      "& .rbc-today": { backgroundColor: palette.calendarToday },
      "& .rbc-show-more": {
        backgroundColor: "transparent",
        color: palette.primaryLight,
        fontWeight: 600,
      },
      "& .rbc-show-more:hover, & .rbc-show-more:focus": { color: palette.primaryLight },

      // ── Vista semana / día ──
      "& .rbc-time-view .rbc-time-gutter, & .rbc-time-view .rbc-label": { color: palette.textSecondary },
      "& .rbc-time-header, & .rbc-time-header.rbc-overflowing, & .rbc-time-header-content, & .rbc-time-content, & .rbc-time-content > * + * > *": {
        borderColor: border,
      },
      "& .rbc-timeslot-group": { borderBottom: `1px solid ${border}` },
      "& .rbc-day-slot .rbc-time-slot": { borderTop: `1px solid ${border}` },
      "& .rbc-current-time-indicator": { backgroundColor: palette.danger, height: 2 },

      // ── Vista agenda ──
      "& .rbc-agenda-view table.rbc-agenda-table": { border: `1px solid ${border}` },
      "& .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td": { borderLeft: `1px solid ${border}` },
      "& .rbc-agenda-view table.rbc-agenda-table tbody > tr + tr": { borderTop: `1px solid ${border}` },
      "& .rbc-agenda-view table.rbc-agenda-table thead > tr > th": {
        borderBottom: `1px solid ${border}`,
        backgroundColor: sunken,
        color: palette.textSecondary,
        fontWeight: 600,
      },
      "& .rbc-agenda-empty": { color: palette.textSecondary, padding: theme.spacing(2) },

      // ── Eventos ──
      "& .rbc-event": {
        backgroundColor: palette.calendarEvent,
        color: palette.calendarEventText,
        border: "none",
        borderRadius: radiusSm,
        padding: "2px 6px",
      },
      "& .rbc-event.rbc-selected": { backgroundColor: palette.calendarEventSelected },
      "& .rbc-event:focus": { outline: `2px solid ${palette.primaryLight}`, outlineOffset: 1 },
      "& .rbc-day-slot .rbc-background-event, & .rbc-day-slot .rbc-event": {
        border: `1px solid ${palette.calendarEventSelected}`,
      },

      // ── Popup "+N más" (se monta dentro del contenedor del calendario) ──
      "& .rbc-overlay": {
        backgroundColor: palette.overlay,
        border: `1px solid ${border}`,
        borderRadius: radiusSm,
        boxShadow: shadows.dropdown,
        color: palette.textPrimary,
      },
      "& .rbc-overlay-header": {
        borderBottom: `1px solid ${border}`,
        color: palette.textSecondary,
      },
    },
    calendarEvent: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      fontFamily: (a.typography && a.typography.fontFamily) || theme.typography.fontFamily
    },
    // Historial (0260)
    historialTableWrap: {
      borderRadius: radius,
      backgroundColor: surface,
      boxShadow: shadows.card,
      border: `1px solid ${border}`,
      overflow: "auto",
      maxHeight: "calc(100vh - 320px)"
    },
    historialTh: {
      fontWeight: 600,
      color: palette.textSecondary,
      backgroundColor: sunken,
      borderBottom: `1px solid ${border}`
    },
    historialRow: {
      "&:hover": { backgroundColor: palette.hover }
    },
    historialCard: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      borderRadius: radius,
      backgroundColor: surface,
      boxShadow: shadows.card,
      border: `1px solid ${border}`
    }
  };
});
